import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { toast, ToastContainer } from 'react-toastify';
import useHandleErrors from '../../utils/user.utils';
import AddVendorModal from './AddVendorModal';
import ViewVendorModal from './ViewVendorModal';
import EditVendorModal from './EditVendorModal';

const Partners = () => {
    const [vendors, setVendors] = useState([]);
    const [filteredVendors, setFilteredVendors] = useState([]);
    const [searchQuery, setSearchQuery] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [recordsPerPage, setRecordsPerPage] = useState(10);
    const [showAddModal, setShowAddModal] = useState(false);
    const [showViewModal, setShowViewModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [selectedVendor, setSelectedVendor] = useState(null);
    const [loading, setLoading] = useState(false);

    const storedToken = localStorage.getItem("token");
    const handleErrors = useHandleErrors();
    const hasFetchedRef = useRef(false);

    useEffect(() => {
        const preloading = document.getElementById("preloading");
        setTimeout(() => {
            if (preloading) {
                preloading.style.display = "none";
            }
        }, 400);
    }, []);

    const fetchVendors = async () => {
        setLoading(true);
        try {
            const path = "/getpartnervendoronDetails";
            const URL = process.env.REACT_APP_BE_URL + path;
            const response = await axios.get(URL, {
                headers: {
                    Authorization: `Bearer ${storedToken}`,
                },
            });
            if (response.status === 200) {
                // Filter only vendors
                const all = response.data.partnerVendorDetails || [];
                const vendors = all.filter(item => item.UserType === "Vendor");
                setVendors(vendors);
                setFilteredVendors(vendors);
            } else {
                handleErrors(response);
            }
        } catch (error) {
            if (error.response) {
                handleErrors(error.response);
            } else {
                console.error("Error fetching vendors:", error);
                toast.error("Failed to fetch vendors");
            }
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (!hasFetchedRef.current) {
            fetchVendors();
            hasFetchedRef.current = true;
        }
    }, []);

    useEffect(() => {
        setFilteredVendors(
            vendors.filter(vendor =>
                vendor.PartnerName?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                vendor.RegistrationNumber?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                vendor.Email?.toLowerCase().includes(searchQuery.toLowerCase()) 
                )
                // partner.PartnerManager?.toLowerCase().includes(searchQuery.toLowerCase())
            
        );
        setCurrentPage(1);
    }, [searchQuery, vendors]);

    const handleAddVendor = async (vendorData) => {
        try {
            const path = "/partnervendoronboard";
            const URL = process.env.REACT_APP_BE_URL + path;
            const response = await axios.post(URL, vendorData, {
                headers: {
                    Authorization: `Bearer ${storedToken}`,
                    'Content-Type': 'multipart/form-data',
                },
            });
            if (response.status === 201 || response.status === 200) {
                toast.success("Vendor Onboarded successfully");
                fetchVendors();
                setShowAddModal(false);
            } else {
                handleErrors(response);
            }
        } catch (error) {
            if (error.response) {
                handleErrors(error.response);
            } else {
                console.error("Error creating partner:", error);
                toast.error("Failed to create partner");
            }
        }
    };

    const handleUpdateVendor = async (partnerData) => {
        try {
            const path = `/updatepartnervendor`;
            const URL = process.env.REACT_APP_BE_URL + path;
            const response = await axios.put(URL, partnerData, {
                headers: {
                    Authorization: `Bearer ${storedToken}`,
                    'Content-Type': 'multipart/form-data',
                },
            });
            if (response.status === 200) {
                toast.success("Vendor updated successfully");
                fetchVendors();
                setShowEditModal(false);
                setSelectedVendor(null);
            } else {
                handleErrors(response);
            }
        } catch (error) {
            if (error.response) {
                handleErrors(error.response);
            } else {
                console.error("Error updating partner:", error);
                toast.error("Failed to update partner");
            }
        }
    };

    const handleViewVendor = (partner) => {
        setSelectedVendor(partner);
        setShowViewModal(true);
    };

    const handleEditVendor = (vendor) => {
        setSelectedVendor(vendor);
        setShowEditModal(true);
    };

    // Pagination logic
    const indexOfLastRecord = currentPage * recordsPerPage;
    const indexOfFirstRecord = indexOfLastRecord - recordsPerPage;
    const currentRecords = filteredVendors.slice(indexOfFirstRecord, indexOfLastRecord);
    const totalPages = Math.ceil(filteredVendors.length / recordsPerPage);

    const paginate = (pageNumber) => setCurrentPage(pageNumber);

    return (
        <div>
            {loading && (
                <div id="preloading">
                    <span className="loader"></span>
                </div>
            )}
            <div className="content-page">
                <div className="container-fluid">
                    <div className="row mb-2">
                        <div className="col">
                            <h3 className="title mb-2">Vendor Management</h3>
                        </div>
                        <div className="col-auto">
                            <button
                                className="btn btn-primary mt-lg-0 mt-4"
                                onClick={() => setShowAddModal(true)}
                            >
                                <i className="ri-add-line"></i> Add Vendor
                            </button>
                        </div>
                    </div>
                    {vendors?.length === 0 ? (
                        <div className="container">
                            <div className="row vh-50 justify-content-center align-items-center">
                                <div className="col-auto">
                                    <div className="no-data-wrapper">
                                        <div className="no-data-found">
                                            <i className="ri-file-damage-fill"></i>
                                            <div className="bounce"><i className="ri-search-eye-line"></i></div>
                                            <p>No Data Found</p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    ) : (
                        <div>
                            <div className="row align-items-center justify-content-center mb-4">
                                <div className="col-lg-5 col-md-8 col-sm-10">
                                    <div className="container-fluid row-rounded mb-0">
                                        <div className="row px-2 py-0">
                                            <div className="input-group mb-0">
                                                <input
                                                    type="search"
                                                    className="form-control search-input"
                                                    placeholder="Keyword search..."
                                                    value={searchQuery}
                                                    onChange={(e) => setSearchQuery(e.target.value)}
                                                />
                                                <div className="input-group-append">
                                                    <button className="btn btn-primary" type="button" title="Search" rel="tooltip">
                                                        <i className="ri-menu-search-line"></i>
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                    <small className="form-text text-muted text-center mt-0">
                                        <i className="ri-information-2-fill text-info"></i> Allows: Vendor Name - Registration Number - Email
                                    </small>
                                </div>
                            </div>
                            <div className="row">
                                <div className="col-lg-12">
                                    <div className="card mb-4">
                                        <div className="card-body">
                                            <div className="row">
                                                <div className="col">
                                                    <h5 className="title mt-1 mb-0">Summary</h5>
                                                </div>                                             
                                            </div>
                                            <div className="row mt-3">
                                                <div className="col-md-12">
                                                    <div className="table-responsive leave-information">
                                                        <table className="table grid">
                                                            <thead className="bg-light">
                                                                <tr>
                                                                    <th>S.No.</th>
                                                                    <th>Vendor Name</th>
                                                                    <th>Registration Number</th>
                                                                    <th>Contact Number</th>
                                                                    <th>Email</th>
                                                                    <th>Purchasing Manager</th>
                                                                    <th></th>
                                                                </tr>
                                                            </thead>
                                                            <tbody>
                                                                {currentRecords.length > 0 ? (
                                                                    currentRecords.map((vendor, index) => (
                                                                        <tr key={vendor.id || index}>
                                                                            <td>{indexOfFirstRecord + index + 1}</td>
                                                                            <td>{vendor.PartnerName}</td>
                                                                            <td>{vendor.RegistrationNumber}</td>
                                                                            <td>{vendor.ContactNumber}</td>
                                                                            <td
                                                                                style={{
                                                                                    maxWidth: '150px',
                                                                                    whiteSpace: 'nowrap',
                                                                                    overflow: 'hidden',
                                                                                    textOverflow: 'ellipsis'
                                                                                }}
                                                                                title={vendor.Email} // full email on hover
                                                                            >
                                                                                {vendor.Email}
                                                                            </td>

                                                                            <td
                                                                                style={{
                                                                                    maxWidth: '150px',
                                                                                    whiteSpace: 'nowrap',
                                                                                    overflow: 'hidden',
                                                                                    textOverflow: 'ellipsis'
                                                                                }}
                                                                                title={vendor.PartnerManager} // full text on hover
                                                                            >
                                                                                {vendor.PartnerManager}
                                                                            </td>

                                                                            <td className="text-center">
                                                                                <span>
                                                                                    <button
                                                                                        className="btn btn-link text-info btn-action"
                                                                                        type="button"
                                                                                        title="View Vendor"
                                                                                        rel="tooltip"
                                                                                        onClick={() => handleViewVendor(vendor)}
                                                                                    >
                                                                                        <i className="ri-eye-line"></i>
                                                                                    </button>
                                                                                    <button
                                                                                        className="btn btn-link text-warning btn-action"
                                                                                        type="button"
                                                                                        title="Edit Vendor"
                                                                                        rel="tooltip"
                                                                                        onClick={() => handleEditVendor(vendor)}
                                                                                    >
                                                                                        <i className="ri-edit-line"></i>
                                                                                    </button>
                                                                                </span>
                                                                            </td>
                                                                        </tr>
                                                                    ))
                                                                ) : (
                                                                    <tr>
                                                                        <td colSpan="8" className="text-center">
                                                                            No vendors found
                                                                        </td>
                                                                    </tr>
                                                                )}
                                                            </tbody>
                                                        </table>
                                                    </div>
                                                </div>
                                                <div className="col-md-12">
                                                    <div className="container-fluid">
                                                        <div className="row border-top pt-3">
                                                            <div className="col-auto">
                                                                <p className="mt-1 mb-0">Records</p>
                                                            </div>
                                                            <div className="col-1 record-drop">
                                                                <select
                                                                    className="form-control"
                                                                    value={recordsPerPage}
                                                                    onChange={(e) => setRecordsPerPage(Number(e.target.value))}
                                                                >
                                                                    <option value="5">5</option>
                                                                    <option value="10">10</option>
                                                                    <option value="15">15</option>
                                                                    <option value="20">20</option>
                                                                    <option value="25">25</option>
                                                                </select>
                                                            </div>
                                                            <div className="col">
                                                                <nav>
                                                                    <ul className="pagination justify-content-center">
                                                                        <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                                                                            <button className="page-link" onClick={() => paginate(1)} tabIndex="-1">
                                                                                <i className="ri-arrow-left-double-line"></i>
                                                                            </button>
                                                                        </li>
                                                                        <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                                                                            <button className="page-link" onClick={() => paginate(currentPage - 1)} tabIndex="-1">
                                                                                <i className="ri-arrow-left-s-line"></i>
                                                                            </button>
                                                                        </li>
                                                                        {Array.from({ length: totalPages }, (_, index) => (
                                                                            <li key={index + 1} className={`page-item ${index + 1 === currentPage ? 'active' : ''}`}>
                                                                                <button className="page-link" onClick={() => paginate(index + 1)}>
                                                                                    {index + 1}
                                                                                </button>
                                                                            </li>
                                                                        ))}
                                                                        <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                                                                            <button className="page-link" onClick={() => paginate(currentPage + 1)}>
                                                                                <i className="ri-arrow-right-s-line"></i>
                                                                            </button>
                                                                        </li>
                                                                        <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                                                                            <button className="page-link" onClick={() => paginate(totalPages)}>
                                                                                <i className="ri-arrow-right-double-fill"></i>
                                                                            </button>
                                                                        </li>
                                                                    </ul>
                                                                </nav>
                                                            </div>
                                                            <div className="col-auto">
                                                                <p className="mt-1 mb-0">Page Showing {currentPage} of {totalPages}</p>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* Modals */}
            <AddVendorModal
                show={showAddModal}
                onHide={() => setShowAddModal(false)}
                onSubmit={handleAddVendor}
            />

            <ViewVendorModal
                show={showViewModal}
                onHide={() => {
                    setShowViewModal(false);
                    setSelectedVendor(null);
                }}
                vendor={selectedVendor}
            />

            <EditVendorModal
                show={showEditModal}
                onHide={() => {
                    setShowEditModal(false);
                    setSelectedVendor(null);
                }}
                vendor={selectedVendor}
                onSubmit={handleUpdateVendor}
            />

            <ToastContainer />
        </div>
    );
};

export default Partners;
